import { useState } from "react";
import { Handshake, X } from "lucide-react";
import { Button } from "@/components/ui/button";

type Reserva = {
  id: number | string;
  tutor_nome?: string;
  servico_nome?: string;
  status?: string;
};

type CollabOption = {
  id: number;
  anfitriao_id: number;
  anfitriao_nome: string;
  cidade?: string | null;
  estado?: string | null;
  servico_nome: string;
  preco_base: number;
};

type Props = {
  reserva: Reserva;
  onSent?: (reservaId: string | number) => void;
  size?: "sm" | "default";
  variant?: "outline" | "ghost" | "default";
  className?: string;
};

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

async function fetchJson(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.message || "Não foi possível concluir a operação.");
  }

  return data;
}

export default function ReservaCollabAction({
  reserva,
  onSent,
  size = "sm",
  variant = "outline",
  className = "text-xs h-8",
}: Props) {
  const [open, setOpen] = useState(false);
  const [collabOptions, setCollabOptions] = useState<CollabOption[]>([]);
  const [selectedServicoId, setSelectedServicoId] = useState<string>("");
  const [reason, setReason] = useState("Sem disponibilidade");
  const [message, setMessage] = useState(
    "Olá! Recebi esta solicitação, mas não consigo atender. Você consegue avaliar essa reserva?"
  );
  const [modalLoading, setModalLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (reserva.status !== "pendente") {
    return null;
  }

  async function openModal() {
    setOpen(true);
    setCollabOptions([]);
    setSelectedServicoId("");
    setFeedback(null);
    setError(null);
    setModalLoading(true);

    try {
      const data = await fetchJson(`/anfitriao/reservas/${reserva.id}/colabs/options`);
      const options = data?.options || [];
      setCollabOptions(options);
      if (options.length > 0) {
        setSelectedServicoId(String(options[0].id));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar colegas para colab.");
    } finally {
      setModalLoading(false);
    }
  }

  function closeModal() {
    if (submitting) return;
    setOpen(false);
    setCollabOptions([]);
    setSelectedServicoId("");
    setFeedback(null);
    setError(null);
  }

  async function submitCollab() {
    if (!selectedServicoId) return;

    setSubmitting(true);
    setFeedback(null);
    setError(null);

    try {
      await fetchJson(`/anfitriao/reservas/${reserva.id}/colabs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrfToken(),
        },
        body: JSON.stringify({
          to_servico_id: Number(selectedServicoId),
          reason,
          message,
        }),
      });

      setFeedback("Colab enviada ao tutor para aprovação.");
      onSent?.(reserva.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível criar a colab.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button type="button" variant={variant} size={size} className={className} onClick={openModal}>
        <Handshake className="mr-1 h-3.5 w-3.5" />
        Encaminhar
      </Button>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-background p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">Encaminhar reserva para colega</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Reserva #{reserva.id} • {reserva.servico_nome || "Serviço"} • {reserva.tutor_nome || "Tutor"}
                </p>
              </div>

              <button
                type="button"
                className="rounded-full p-1 text-muted-foreground hover:bg-muted"
                onClick={closeModal}
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {error ? (
              <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
            ) : null}

            {feedback ? (
              <div className="mb-3 rounded-lg bg-green-50 p-3 text-sm text-green-700">{feedback}</div>
            ) : null}

            {modalLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Carregando colegas disponíveis...
              </div>
            ) : collabOptions.length > 0 ? (
              <div className="space-y-4">
                <label className="block text-sm font-medium">
                  Colega / serviço destino
                  <select
                    className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={selectedServicoId}
                    onChange={(event) => setSelectedServicoId(event.target.value)}
                  >
                    {collabOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.anfitriao_nome} — {option.servico_nome}
                        {option.cidade ? ` — ${option.cidade}${option.estado ? `/${option.estado}` : ""}` : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-sm font-medium">
                  Motivo
                  <select
                    className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  >
                    <option>Sem disponibilidade</option>
                    <option>Fora da região</option>
                    <option>Serviço incompatível</option>
                    <option>Pet exige cuidado específico</option>
                    <option>Outro</option>
                  </select>
                </label>

                <label className="block text-sm font-medium">
                  Mensagem opcional
                  <textarea
                    className="mt-1 min-h-24 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                  />
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={closeModal} disabled={submitting}>
                    Fechar
                  </Button>
                  <Button type="button" onClick={submitCollab} disabled={submitting || !!feedback}>
                    {submitting ? "Enviando..." : "Enviar colab"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                  Nenhum colega ativo com o mesmo tipo de serviço foi encontrado para esta reserva.
                </div>
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" onClick={closeModal}>
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
